import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Leaf, Menu, X, ArrowUpRight } from "lucide-react";

const navLinks = [
  { to: "/gesundheit", label: "Gesundheit", accent: "sage" },
  { to: "/finanzen", label: "Finanzen", accent: "clay" },
  { to: "/ueber-mich", label: "Über mich", accent: "honey" },
  { to: "/kontakt", label: "Kontakt", accent: "clay" },
];

const dotColor = {
  sage: "bg-sage-400",
  clay: "bg-clay-400",
  honey: "bg-honey-400",
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-bone-100/85 border-b border-bone-300/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group shrink-0"
        >
          <span className="relative w-10 h-10 rounded-full bg-sage-300 text-sage-700 flex items-center justify-center group-hover:rotate-[20deg] transition-transform duration-500">
            <Leaf strokeWidth={1.75} className="w-4 h-4" />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-honey-400 border-2 border-bone-100 group-hover:scale-125 transition-transform" />
          </span>
          <span className="font-display text-xl text-ink-900 tracking-tight leading-none">
            <span className="block">Xenia</span>
            <span className="block italic text-sage-600 -mt-1">Höfel</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 bg-bone-100/80 backdrop-blur-sm rounded-full px-2 py-1.5 border border-bone-300/70">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/ü/g, "u").replace(/ä/g, "a").replace(/ö/g, "o").replace(/ß/g, "ss")}`}
              className={({ isActive }) =>
                `group relative overflow-hidden inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] tracking-wide transition-colors duration-300 ${
                  isActive
                    ? "bg-ink-900 text-bone-50"
                    : "text-ink-700 hover:text-ink-900"
                }`
              }
              end={l.to === "/"}
            >
              {({ isActive }) => (
                <>
                  <span className={`w-1.5 h-1.5 rounded-full transition-transform duration-500 ${
                    isActive ? "bg-honey-400" : dotColor[l.accent]
                  } group-hover:scale-150`} />
                  {/* dual-stack text hover */}
                  <span className="relative h-[18px] overflow-hidden inline-block">
                    <span className="block transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:-translate-y-full">
                      {l.label}
                    </span>
                    <span className="block absolute inset-0 translate-y-full transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:translate-y-0 italic">
                      {l.label}
                    </span>
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <a
          href="https://outlook.office.com/bookwithme/user/b7225bf50f9b4b979c1b0aea80d80018@dvag.de?anonymous&ismsaljsauthenabled&ep=plink"
          target="_blank"
          rel="noreferrer"
          data-testid="nav-cta-contact"
          className="hidden md:inline-flex items-center gap-2 pl-5 pr-2 h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-bone-50 text-[13px] tracking-wide transition-all duration-300 hover:-translate-y-0.5"
        >
          Termin vereinbaren
          <span className="w-8 h-8 rounded-full bg-honey-400 text-ink-900 flex items-center justify-center">
            <ArrowUpRight strokeWidth={1.75} className="w-3.5 h-3.5" />
          </span>
        </a>

        <button
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden w-11 h-11 rounded-full border border-bone-300 flex items-center justify-center bg-bone-100/80 backdrop-blur-sm"
        >
          {open ? (
            <X strokeWidth={1.75} className="w-5 h-5 text-ink-900" />
          ) : (
            <Menu strokeWidth={1.75} className="w-5 h-5 text-ink-900" />
          )}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-bone-300/70 bg-bone-100/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `flex items-center justify-between px-4 py-3 rounded-2xl transition-colors ${
                    isActive ? "bg-ink-900 text-bone-50" : "text-ink-700 hover:bg-bone-200"
                  }`
                }
              >
                <span className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${dotColor[l.accent]}`} />
                  <span className="text-base">{l.label}</span>
                </span>
                <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
              </NavLink>
            ))}
            <a
              href="https://outlook.office.com/bookwithme/user/b7225bf50f9b4b979c1b0aea80d80018@dvag.de?anonymous&ismsaljsauthenabled&ep=plink"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 h-12 rounded-full bg-sage-500 text-bone-50 text-sm"
            >
              Termin vereinbaren
              <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
